/**
 * Codespace forwarded-port visibility (GitHub backend only).
 *
 * GitHub auto-forwards a port the moment something binds it, but a port that
 * isn't declared in devcontainer.json is forwarded **private** — the
 * `{codespaceName}-{port}.app.github.dev` URL the preview endpoint hands a
 * teammate 404s until the visibility is flipped. Runtime-discovered ports are
 * by definition not declared, so every port the poller reports needs one
 * best-effort `gh codespace ports visibility` call.
 *
 * Rules this module holds to:
 * - No-op unless we are actually inside a codespace (`CODESPACES=true` and
 *   `CODESPACE_NAME` set). On a GKE pod there is nothing to flip.
 * - `org` first, `public` as the fallback — some orgs forbid org-visible ports,
 *   and `public` is what `scripts/load-env.sh` already does for 3050/7090.
 * - One attempt per port per process, and NEVER throw: this runs off the
 *   port-discovery poller, which must not be broken by a missing `gh` binary.
 */
import { execFile } from "node:child_process";

/** Result of one `gh` invocation. `ok` false carries stderr for the log line. */
export interface VisibilityCommandResult {
  ok: boolean;
  stderr: string;
}

export interface CodespacePortVisibilityOptions {
  env?: NodeJS.ProcessEnv;
  /** Command runner override for tests (no spawn). */
  run?: (args: readonly string[]) => Promise<VisibilityCommandResult>;
  log?: (message: string) => void;
}

const GH_TIMEOUT_MS = 15_000;
/** Tried in order; the first that succeeds wins. */
const VISIBILITIES = ["org", "public"] as const;

function runGh(args: readonly string[]): Promise<VisibilityCommandResult> {
  return new Promise((resolve) => {
    execFile("gh", [...args], { timeout: GH_TIMEOUT_MS }, (error, _stdout, stderr) => {
      resolve({ ok: !error, stderr: (stderr || (error ? String(error.message) : "")).trim() });
    });
  });
}

/** Are we running inside a GitHub Codespace (vs a GKE claudespace pod)? */
export function isCodespaceEnvironment(env: NodeJS.ProcessEnv = process.env): boolean {
  return env.CODESPACES === "true" && !!env.CODESPACE_NAME;
}

/**
 * Best-effort flipper for runtime-discovered forwarded ports. Construct once
 * and call `ensureVisible` after every successful discovered-ports report.
 */
export class CodespacePortVisibility {
  private readonly env: NodeJS.ProcessEnv;
  private readonly run: (args: readonly string[]) => Promise<VisibilityCommandResult>;
  private readonly log: (message: string) => void;
  /** Ports already attempted (success or failure) — one try per process. */
  private readonly attempted = new Set<number>();

  constructor(options: CodespacePortVisibilityOptions = {}) {
    this.env = options.env ?? process.env;
    this.run = options.run ?? runGh;
    this.log = options.log ?? ((m) => process.stderr.write(`[conveyor-agent] ${m}\n`));
  }

  /** Flip every not-yet-attempted port. Resolves even when everything fails. */
  async ensureVisible(ports: readonly number[]): Promise<void> {
    if (!isCodespaceEnvironment(this.env)) return;
    const codespaceName = this.env.CODESPACE_NAME as string;
    for (const port of ports) {
      if (this.attempted.has(port)) continue;
      this.attempted.add(port);
      await this.flip(port, codespaceName);
    }
  }

  private async flip(port: number, codespaceName: string): Promise<void> {
    let lastError = "";
    for (const visibility of VISIBILITIES) {
      const result = await this.run([
        "codespace",
        "ports",
        "visibility",
        `${port}:${visibility}`,
        "-c",
        codespaceName,
      ]).catch((error: unknown) => ({
        ok: false,
        stderr: error instanceof Error ? error.message : String(error),
      }));
      if (result.ok) {
        this.log(`Forwarded port ${port} set to ${visibility} visibility`);
        return;
      }
      lastError = result.stderr;
    }
    this.log(
      `Could not change visibility of forwarded port ${port} — the preview URL may 404 ` +
        `for other users${lastError ? `: ${lastError}` : ""}`,
    );
  }
}
